// SPDX-License-Identifier: AGPL-3.0-or-later

import {SnowflakeStringType} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

export enum ActivityType {
	Playing = 0,
	Streaming = 1,
	Listening = 2,
	Watching = 3,
	Custom = 4,
	Competing = 5,
}

export const ActivityTypeSchema = z.nativeEnum(ActivityType).describe('The type of activity');

export const ActivityTimestampsSchema = z
	.object({
		start: z.number().int().optional().describe('Unix timestamp in milliseconds when the activity started'),
		end: z.number().int().optional().describe('Unix timestamp in milliseconds when the activity will end'),
	})
	.describe('Timestamps for the activity');

export type ActivityTimestamps = z.infer<typeof ActivityTimestampsSchema>;

export const ActivityPartySchema = z
	.object({
		id: z.string().max(128).optional().describe('The ID of the party'),
		size: z.tuple([z.number().int().min(0), z.number().int().min(1)]).optional().describe('Party current and max size: [current_size, max_size]'),
	})
	.describe('Information about the party/group');

export type ActivityParty = z.infer<typeof ActivityPartySchema>;

export const ActivityAssetsSchema = z
	.object({
		large_image: z.string().max(256).optional().describe('Large image asset ID or URL'),
		large_text: z.string().max(128).optional().describe('Hover text for the large image'),
		small_image: z.string().max(256).optional().describe('Small image asset ID or URL'),
		small_text: z.string().max(128).optional().describe('Hover text for the small image'),
	})
	.describe('Images and hover text for the presence');

export type ActivityAssets = z.infer<typeof ActivityAssetsSchema>;

export const ActivitySecretsSchema = z
	.object({
		join: z.string().max(128).optional().describe('Secret for joining a party'),
		spectate: z.string().max(128).optional().describe('Secret for spectating a game'),
		match: z.string().max(128).optional().describe('Secret for joining a match'),
	})
	.describe('Secrets for Rich Presence joining and spectating');

export type ActivitySecrets = z.infer<typeof ActivitySecretsSchema>;

export const ActivityButtonSchema = z
	.object({
		label: z.string().min(1).max(32).describe('Text shown on the button'),
		url: z.string().url().max(512).describe('URL opened when clicking the button'),
	})
	.describe('Custom button for the Rich Presence card');

export type ActivityButton = z.infer<typeof ActivityButtonSchema>;

export const ActivityEmojiSchema = z
	.object({
		name: z.string().max(64).describe('The name of the emoji'),
		id: SnowflakeStringType.optional().describe('The ID of the custom emoji'),
		animated: z.boolean().optional().describe('Whether the emoji is animated'),
	})
	.describe('Emoji associated with the activity');

export type ActivityEmoji = z.infer<typeof ActivityEmojiSchema>;

export const ActivitySchema = z.object({
	name: z.string().min(1).max(128).describe('Activity name (e.g. game title)'),
	type: ActivityTypeSchema.default(ActivityType.Playing),
	url: z.string().url().max(512).nullish().optional().describe('Stream URL if streaming'),
	created_at: z.number().int().optional().describe('Unix timestamp in milliseconds when activity was added'),
	timestamps: ActivityTimestampsSchema.optional(),
	application_id: SnowflakeStringType.optional().describe('Application ID for the Rich Presence game/app'),
	details: z.string().max(128).nullish().optional().describe('What the player is currently doing (e.g. In a match)'),
	state: z.string().max(128).nullish().optional().describe('Current party status or details (e.g. In a group)'),
	emoji: ActivityEmojiSchema.nullish().optional(),
	party: ActivityPartySchema.optional(),
	assets: ActivityAssetsSchema.optional(),
	secrets: ActivitySecretsSchema.optional(),
	instance: z.boolean().optional().describe('Whether the activity is an instanced game session'),
	flags: z.number().int().optional().describe('Activity flags bitfield'),
	buttons: z.array(ActivityButtonSchema).max(2).optional().describe('Up to 2 custom clickable buttons'),
});

export type Activity = z.infer<typeof ActivitySchema>;

export const PresenceUpdatePayloadSchema = z.object({
	since: z.number().int().nullable().optional(),
	activities: z.array(ActivitySchema).optional().default([]),
	status: z.enum(['online', 'dnd', 'idle', 'invisible', 'offline']).default('online'),
	afk: z.boolean().optional().default(false),
});

export type PresenceUpdatePayload = z.infer<typeof PresenceUpdatePayloadSchema>;
